import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { PopupEvent } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { isAlive } from 'webapp/utils/common';

import joinClasses from 'webapp/helpers/join-classes';
import BarLoaderComponent from 'webapp/components/bar-loader';
import ClientMetricsDataComponent from 'webapp/components/client/metrics/data';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import ToolTipComponent from 'webapp/components/tool-tip';

import type { ClientMetric } from 'webapp/models-custom';
import type DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';

type Action = 'editMetric' | 'removeMetric';

interface Args {
	position: number;
	spaceId: number | null;
	model?: ClientMetric;
}

export default class ClientMetricsItemComponent extends Component<Args> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('request') declare requestService: Service.Request;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/client-metrics')
	declare mmClientMetricsService: Service.ModelManager.ClientMetrics;

	@tracked contextOpened = false;

	get model() {
		return this.args.model;
	}

	get isEmpty() {
		return !this.model;
	}

	get isLoading() {
		const model = this.model;
		return !this.isEmpty && !model?.sample && !model?.isGeneric;
	}

	get noData() {
		const model = this.model;
		return model?.isGeneric || !this.primaryPoint;
	}

	@cached
	get primaryPoint(): DChartPrimaryPoint | null {
		const model = this.model;

		if (!model?.dataSelect) {
			return null;
		}

		const dataRoot = model.dataSelect.dataRoot;
		const outputs = dataRoot?.outputs;

		return outputs?.length
			? model.mmSamplesService.createDChartPointsFromOutput(outputs[0]!)[0]!
			: null;
	}

	get canEdit() {
		return this.currentUserService.isAdmin && !this.currentUserService.isDemo;
	}

	get allowContext() {
		const model = this.model;
		return !this.isLoading && !model?.isGeneric && !model?.mustChooseSource;
	}

	get showContext() {
		return this.allowContext && this.canEdit && !this.isEmpty;
	}

	get showInfoTooltip() {
		return this.allowContext && !this.canEdit && !this.currentUserService.isDemo;
	}

	get contextOptions() {
		const options = [];

		if (!this.isEmpty) {
			options.push(
				{
					icon: 'edit',
					text: 'Edit metric',
					action: 'editMetric',
				},
				{
					icon: 'delete',
					text: 'Remove',
					action: 'removeMetric',
				},
			);
		}

		return options;
	}

	@action
	removeMetric() {
		if (this.model) {
			this.model
				.delete()
				.catch(this.requestService.handleGenericThrownError.bind(this.requestService));

			this.analyticsService.event(TrackingEvent.CLIENT_KPI_TILE_DELETE);
		}
	}

	@action
	editMetric() {
		this.openMetricsPopup();
	}

	@action
	selectSource() {
		const model = this.model;
		this.openMetricsPopup(model?.sourceTypeKey || '');
	}

	@action
	openContext(e: PointerEvent) {
		if (this.contextOpened) {
			return;
		}

		const attachTo = e.target as HTMLElement,
			popup = this.popupService.createListPopup(
				attachTo,
				undefined,
				'intervals popup-client-favorite',
			);

		if (popup) {
			popup.open({
				itemsList: this.contextOptions,
				doAction: (actionName: Action) => {
					this[actionName]();
				},
			});

			this.contextOpened = true;
			popup.on(PopupEvent.CLOSED, this, () => {
				if (isAlive(this)) {
					this.contextOpened = false;
				}
			});
		}
	}

	private openMetricsPopup(onlySourceOfType?: string) {
		const spaceId = this.args.spaceId;

		if (!spaceId) {
			return;
		}

		const model = this.model;

		this.mmClientMetricsService.openClientMetricsPopup({
			model: model ? this.mmClientMetricsService.createModelFromRecord(model.model) : null,
			position: this.args.position,
			spaceId,
			onlySourceOfType,
		});
	}
	<template>
		<li class={{joinClasses (array "fav" (if this.contextOpened "contextOpened"))}}>
			<div class="ct {{if this.noData 'no-data' 'has-data'}}">
				{{#if this.isLoading}}
					<BarLoaderComponent />
				{{else}}
					{{#if this.isEmpty}}
						{{#if this.canEdit}}
							<a role="button" {{on "click" this.editMetric}}>SELECT METRIC</a>
						{{else}}
							EMPTY
						{{/if}}
					{{else if @model.isGeneric}}
						{{#if @model.mustChooseSource}}
							<p class="selectSource">
								This account has more than one
								{{@model.sourceTypeName}}
								connection.
								<a role="button" {{on "click" this.selectSource}}>Select profile</a>
							</p>
						{{else}}
							<div class="data not-applicable">
								N/A
							</div>
						{{/if}}
					{{else if @model}}
						<ClientMetricsDataComponent
							@primaryPoint={{this.primaryPoint}}
							@isSyncing={{@model.isNumberSyncing}}
							@sourceInfo={{@model.sourceInfo}}
							@attributeName={{@model.attributeName}}
							@metricName={{@model.metricName}}
						/>
					{{else}}
						NO DATA
					{{/if}}
				{{/if}}

				{{#if this.showContext}}
					<a role="button" class="context" {{on "click" this.openContext}}>...</a>
				{{/if}}

				{{#if this.showInfoTooltip}}
					<a class="context context--info">
						<span class="info-tooltip-wrapper">
							<CommonUiIconComponent @name="InfoFill" @size="sm" />
							<ToolTipComponent
								@text="Only admins can setup account metrics."
								@theme="light"
								@position="bottom"
								@maxWidth={{190}}
							/>
						</span>
					</a>
				{{/if}}
			</div>
		</li>
	</template>
}
